import React from "react";
import { Center, Icon, Tooltip, TooltipProps } from "@chakra-ui/react";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { getTooltip } from "utils/getMessages";

/**
 * Shared Chakra Tooltip props for help icons. Change once to update every field hint.
 */
export const tooltipHintTooltipProps: Omit<TooltipProps, "children" | "label"> = {
  placement: "top",
  hasArrow: true,
  bg: "gray.700",
  color: "white",
  fontSize: "sm",
  px: 3,
  py: 2,
  borderRadius: "md",
};

/** Reserved hit box (Chakra spacing units) — keeps icons aligned in a row */
export const TOOLTIP_HINT_SLOT_BOX = 6;

/** Default icon color for help hints */
export const TOOLTIP_HINT_ICON_COLOR = "blue.300";

const ICON_BOX: Record<"lg" | "md" | "sm", number> = {
  lg: 6,
  md: 5,
  sm: 4,
};

export type TooltipHintIconProps = {
  tooltipKey: string;
  /** lg = section headers; md = standard fields; sm = dense form labels */
  size?: "lg" | "md" | "sm";
  iconColor?: string;
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
};

/**
 * Info icon + tooltip driven by `getTooltip(tooltipKey)`.
 * Place next to labels inside an `HStack` with `spacing={2}` and `align="center"` — do not add extra margin on the tooltip.
 */
export function TooltipHintIcon({
  tooltipKey,
  size = "md",
  iconColor = TOOLTIP_HINT_ICON_COLOR,
  onClick,
}: TooltipHintIconProps) {
  const iconBox = ICON_BOX[size];

  return (
    <Tooltip label={getTooltip(tooltipKey) || ""} {...tooltipHintTooltipProps}>
      <Center
        as="span"
        cursor="help"
        flexShrink={0}
        boxSize={TOOLTIP_HINT_SLOT_BOX}
        onClick={onClick}
      >
        <Icon as={IoMdInformationCircleOutline} boxSize={iconBox} color={iconColor} />
      </Center>
    </Tooltip>
  );
}
